import supabase from '../../supabaseClient';
import { TABLE_NAMES } from '../../config';
import { getPitchOptionsForCameraSetup } from '../../lib/academyCameras';
import { getFootageLayoutValidationReason } from '../../lib/dualMp4Footage';
import { notifyStats } from '../../lib/notifyStats';

export const DEFAULT_PLAYER_STATS = {
  goals: 0,
  assists: 0,
  shots_on_target: 0,
  shots_off_target: 0,
  chances_created: 0,
  successful_dribbles: 0,
  successful_tib: 0,
  saves: 0
};

export const OUTSIDE_ACADEMY_TEAM = 'Outside Team';
/** @deprecated Legacy select value — treat same as OUTSIDE_ACADEMY_TEAM when loading saved games */
export const LEGACY_OUTSIDE_ACADEMY_TEAM = 'Other Team';

export function isOutsideAcademyTeamSelection(value) {
  return value === OUTSIDE_ACADEMY_TEAM || value === LEGACY_OUTSIDE_ACADEMY_TEAM;
}

/** Today, with the clock rounded down to the last full hour. */
export function unpublishedGameSlot(now = new Date()) {
  const d = new Date(now);
  d.setMinutes(0, 0, 0);
  const gameDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const time = `${String(d.getHours()).padStart(2, '0')}:00`;
  return { gameDate, time };
}

/** One hour after the current hour, on the hour. Rolls to the next day after 23:00. */
export function publishedGameSlot(now = new Date()) {
  const d = new Date(now);
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  const gameDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const time = `${String(d.getHours()).padStart(2, '0')}:00`;
  return { gameDate, time };
}

export function findTeamByName(teams, name) {
  if (!name || !Array.isArray(teams)) return null;
  return teams.find((t) => t.name === name) || null;
}

/** Returns a user-facing reason when match step-2 details are incomplete, or null if valid. */
export function getMatchDetailsStepValidationReason(formData) {
  if (!String(formData?.team1 || '').trim() || isOutsideAcademyTeamSelection(formData.team1)) {
    return 'Please select your academy team.';
  }
  const opponentIsAcademy = Boolean(formData?.opponentIsAcademyTeam)
    || (Boolean(formData?.team2) && !isOutsideAcademyTeamSelection(formData.team2));
  if (opponentIsAcademy) {
    if (!String(formData?.team2 || '').trim() || isOutsideAcademyTeamSelection(formData.team2)) {
      return 'Please select the opposing academy team.';
    }
    if (formData.team1 === formData.team2) {
      return 'Please choose two different academy teams.';
    }
  } else if (!String(formData?.team2CustomName || '').trim()) {
    return 'Please enter the opponent name.';
  }
  if (formData?.publishOnApp !== false) {
    if (!String(formData?.gameDate || '').trim()) {
      return 'Please select a date.';
    }
    if (!String(formData?.time || '').trim()) {
      return 'Please select a time.';
    }
    if (isGameStartInPast(formData.gameDate, formData.time)) {
      return 'Choose a date and time that have not already passed.';
    }
  }
  return null;
}

/** Load roster players for a team (team_members → Users). */
export async function fetchTeamRosterMembers(teamId) {
  if (!teamId) return [];

  const { data: members, error: membersError } = await supabase
    .from(TABLE_NAMES.TEAM_MEMBERS)
    .select('user_id')
    .eq('team_id', teamId);

  if (membersError) throw membersError;

  const userIds = (members || []).map((m) => m.user_id).filter(Boolean);
  if (userIds.length === 0) return [];

  const { data: users, error: usersError } = await supabase
    .from(TABLE_NAMES.USERS)
    .select('user_id, name, email, attendance_name')
    .in('user_id', userIds);

  if (usersError) throw usersError;

  return (users || []).map((u) => ({
    user_id: u.user_id,
    name: u.name || 'Player',
    email: u.email || '',
    attendance_name: u.attendance_name || null,
  }));
}

/** @deprecated Statbox/basic team gating was removed. Kept as a no-op for old imports. */
export function getMatchStatboxBlockReason() {
  return null;
}

/** Returns a user-facing reason when training step-2 details are incomplete, or null if valid. */
export function getTrainingDetailsStepValidationReason(formData) {
  if (!String(formData?.team1 || '').trim()) {
    return 'Please select a team.';
  }
  if (!String(formData?.gameDate || '').trim()) {
    return 'Please select a date.';
  }
  if (!String(formData?.time || '').trim()) {
    return 'Please select a time.';
  }
  if (!String(formData?.venueId || '').trim()) {
    return 'Please select a training location.';
  }
  return null;
}

/** True when URL is a YouTube watch or youtu.be link. */
export function isYoutubeUrl(url) {
  try {
    const parsed = new URL(String(url || '').trim());
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    return host === 'youtube.com' || host === 'youtu.be' || host === 'm.youtube.com';
  } catch {
    return false;
  }
}

/** True when URL is a public Veo match page, for example app.veo.co/matches/… */
export function isVeoMatchUrl(url) {
  try {
    const parsed = new URL(String(url || '').trim());
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    if (host !== 'app.veo.co') return false;
    return /^\/matches\/[^/?#]+\/?$/.test(parsed.pathname);
  } catch {
    return false;
  }
}

export const VIDEO_LINK_HELP = 'YouTube (youtube.com or youtu.be) or a public Veo match (app.veo.co/matches/…).';

/** True for a match video already stored from this upload flow. */
export function isStoredMatchFootageUrl(url) {
  const value = String(url || '').trim();
  if (!/^https?:\/\//i.test(value)) return false;
  return /\/full_match_mp4\//i.test(value) || /\.(mp4|mov|webm|m4v)(\?|$)/i.test(value);
}

/** Returns a user-facing reason when a video link is not YouTube or Veo, or null if valid. */
export function getYoutubeLinkValidationReason(url) {
  const link = String(url || '').trim();
  if (!link) return 'Please enter a YouTube or Veo link.';
  try {
    new URL(link);
  } catch {
    return 'Please enter a valid URL.';
  }
  if (!isYoutubeUrl(link) && !isVeoMatchUrl(link)) {
    return `We only accept ${VIDEO_LINK_HELP}`;
  }
  return null;
}

/** Summary of footage already saved on the game or selected in the upload form. */
export function getExistingFootageSummary({
  match,
  footageFile = null,
  footageUploading = false,
  footageUploadProgress = 0,
  footageUploadStatus = null,
  customLink = '',
  generatedUploadLink = '',
} = {}) {
  if (footageUploading) {
    const compressing = footageUploadStatus === 'compressing';
    return {
      kind: 'uploading',
      title: compressing ? 'Compressing video' : 'Upload in progress',
      detail: compressing
        ? 'Compressing match footage before upload...'
        : footageUploadProgress < 100
          ? `Uploading match footage... ${footageUploadProgress}%`
          : 'Processing upload...',
    };
  }

  if (footageFile) {
    return {
      kind: 'file-pending',
      title: 'File selected',
      detail: `${footageFile.name} (${(footageFile.size / (1024 * 1024)).toFixed(2)} MB)`,
      pending: true,
    };
  }

  let metadata = {};
  try {
    metadata =
      typeof match?.extra_players_metadata === 'string'
        ? JSON.parse(match.extra_players_metadata)
        : match?.extra_players_metadata || {};
  } catch {
    metadata = {};
  }

  if (metadata?.footage_stitch_pending) {
    return {
      kind: 'stitching',
      title: 'Joining clips',
      detail: 'Your videos are being stitched into one match video. Analysis will open when that finishes.',
    };
  }

  const videoLink = String(match?.game_video_link || '').trim();
  const vimeoLink = String(match?.game_video_link_vimeo || '').trim();
  const shareLink = String(generatedUploadLink || match?.upload_link || '').trim();
  const metadataLink = String(metadata?.custom_link_url || '').trim();
  const pendingLink = String(customLink || '').trim();
  const savedYoutube = videoLink && isYoutubeUrl(videoLink) ? videoLink : '';
  const savedVeo = videoLink && isVeoMatchUrl(videoLink) ? videoLink : '';
  const metadataYoutube = metadataLink && isYoutubeUrl(metadataLink) ? metadataLink : '';
  const metadataVeo = metadataLink && isVeoMatchUrl(metadataLink) ? metadataLink : '';
  const veoLink = savedVeo || (!videoLink && isVeoMatchUrl(pendingLink) ? pendingLink : '') || (!videoLink ? metadataVeo : '');
  const youtubeLink = (isYoutubeUrl(pendingLink) ? pendingLink : '') || savedYoutube || metadataYoutube;

  if (vimeoLink) {
    return { kind: 'link-saved', title: 'Vimeo link provided', detail: vimeoLink, url: vimeoLink };
  }
  if (veoLink) {
    return {
      kind: savedVeo ? 'link-saved' : 'link-pending',
      title: 'Veo link',
      detail: veoLink,
      url: veoLink,
      pending: !savedVeo,
    };
  }
  if (videoLink && !isYoutubeUrl(videoLink)) {
    const fileName = decodeURIComponent(videoLink.split('/').pop() || 'Match footage');
    return { kind: 'file-saved', title: 'Footage uploaded', detail: fileName, url: videoLink };
  }
  if (youtubeLink) {
    return {
      kind: isYoutubeUrl(pendingLink) && !savedYoutube ? 'link-pending' : 'link-saved',
      title: 'YouTube link',
      detail: youtubeLink,
      url: youtubeLink,
      pending: Boolean(isYoutubeUrl(pendingLink) && !savedYoutube),
    };
  }
  if (shareLink) {
    return {
      kind: 'share-link',
      title: 'Shareable upload link',
      detail: shareLink,
      url: shareLink,
    };
  }

  return null;
}

function parseMatchMetadata(match) {
  try {
    return typeof match?.extra_players_metadata === 'string'
      ? JSON.parse(match.extra_players_metadata)
      : match?.extra_players_metadata || {};
  } catch {
    return {};
  }
}

/** Footage state for academy under-review match pages. */
export function getUnderReviewFootageStatus(match, { isUploadedMp4SourceUrl } = {}) {
  const videoLink = String(match?.game_video_link || '').trim();
  const vimeoLink = String(match?.game_video_link_vimeo || '').trim();
  const metadata = parseMatchMetadata(match);
  const metadataLink = String(metadata?.custom_link_url || '').trim();
  const hasMetadataYoutube = Boolean(metadataLink && isYoutubeUrl(metadataLink));
  const metadataVeo = metadataLink && isVeoMatchUrl(metadataLink) ? metadataLink : '';
  const isMp4Source = (url) => (
    typeof isUploadedMp4SourceUrl === 'function' ? isUploadedMp4SourceUrl(url) : false
  );

  if (vimeoLink) {
    return { kind: 'vimeo', url: vimeoLink };
  }

  if (videoLink && isVeoMatchUrl(videoLink)) {
    return { kind: 'veo-active', url: videoLink };
  }

  if (videoLink && isYoutubeUrl(videoLink)) {
    return { kind: 'youtube-active', url: videoLink };
  }

  if (videoLink && isMp4Source(videoLink)) {
    if (hasMetadataYoutube) {
      return {
        kind: 'youtube-converted',
        mp4Url: videoLink,
        originalYoutubeUrl: metadataLink,
      };
    }
    return { kind: 'mp4-uploaded', url: videoLink };
  }

  if (hasMetadataYoutube) {
    return { kind: 'youtube-metadata-only', url: metadataLink };
  }

  if (metadataVeo) {
    return { kind: 'veo-metadata-only', url: metadataVeo };
  }

  if (videoLink) {
    return { kind: 'other-uploaded', url: videoLink };
  }

  return { kind: 'none' };
}

export function getMp4FileLabel(url) {
  if (!url) return 'Match footage';
  try {
    return decodeURIComponent(String(url).split('/').pop()?.split('?')[0] || 'Match footage');
  } catch {
    return 'Match footage';
  }
}

/** Returns a user-facing reason when match step-3 recording/location is incomplete, or null if valid. */
export function getMatchRecordingStepValidationReason(
  formData,
  {
    footageFile,
    footageFileRight,
    footageLayout,
    stitchFiles,
    allowMissingFootage = false,
    isPastGame = false,
  } = {},
) {
  const useCustomLink = Boolean(formData?.useCustomLink);

  if (!useCustomLink) {
    if (allowMissingFootage) return null;
    return 'Venue cameras aren’t available. Upload the footage or enter a video link.';
  }

  if (formData.customLinkType === 'upload') {
    return getFootageLayoutValidationReason({
      footageFile,
      footageFileRight,
      footageLayout,
      stitchFiles,
      allowMissing: allowMissingFootage,
    });
  }
  if (formData.customLinkType === 'link') {
    if (isStoredMatchFootageUrl(formData?.customLink)) return null;
    return getYoutubeLinkValidationReason(formData?.customLink);
  }

  return null;
}

/** Parse venue name from stored training_location (may include " · pitch"). */
export function parseTrainingLocationVenueName(trainingLocation) {
  const raw = String(trainingLocation || '').trim();
  if (!raw) return '';
  const sep = raw.indexOf('·');
  return (sep >= 0 ? raw.slice(0, sep) : raw).trim();
}

/**
 * Resolve academy_locations / replay picker id when editing a training session.
 */
export function resolveVenueIdForTrainingSession({ metadata, match, venues }) {
  const list = Array.isArray(venues) ? venues : [];
  const locId = metadata?.academy_location_id;
  if (locId) {
    const prefixed = `academy-location:${locId}`;
    const byMeta = list.find((v) => v.id === prefixed);
    if (byMeta) return byMeta.id;
  }
  if (match?.venueId && list.some((v) => v.id === match.venueId)) {
    return match.venueId;
  }
  const label =
    metadata?.training_location ||
    match?.trainingLocation ||
    match?.venue ||
    '';
  const venueName = parseTrainingLocationVenueName(label);
  if (!venueName) return '';
  const exact = list.find((v) => (v.name || '').trim() === venueName);
  if (exact) return exact.id;
  const lower = venueName.toLowerCase();
  const ci = list.find((v) => (v.name || '').trim().toLowerCase() === lower);
  return ci?.id || '';
}

/** UUID from a merged venue row (academy_locations only). */
export function academyLocationIdFromVenue(venue) {
  if (venue?.academy_location_id) return venue.academy_location_id;
  const id = String(venue?.id || '');
  if (id.startsWith('academy-location:')) {
    return id.slice('academy-location:'.length) || null;
  }
  return null;
}

/** Display string stored in training_location metadata. */
export function formatTrainingLocationLabel(venueName, pitchName) {
  const venue = String(venueName || '').trim();
  const pitch = String(pitchName || '').trim();
  if (!venue) return null;
  if (pitch && pitch !== venue && pitch !== 'Training') {
    return `${venue} · ${pitch}`;
  }
  return venue;
}

/** Default pitch when academy location picker has no pitch/court step. */
export function resolvePitchForVenue(selectedVenueData, formData, pitchOptions = []) {
  if (formData.pitchSlug) {
    const found =
      pitchOptions.find((p) => p.slug === formData.pitchSlug) ||
      (selectedVenueData.pitches || []).find((p) => p.slug === formData.pitchSlug);
    if (found) return found;
  }
  if (formData.customPitchName?.trim()) {
    return { slug: formData.pitchSlug || 'main', name: formData.customPitchName.trim() };
  }
  const name = selectedVenueData?.name || 'Main';
  return { slug: 'main', name };
}

/** Resolve venue + pitch for training without smart court (academy_locations picker). */
export function resolveTrainingVenueSelection({
  formData,
  venues,
  pitchOptions = [],
  academyName,
}) {
  if (formData.venueId) {
    const selectedVenueData = venues.find((v) => v.id === formData.venueId);
    if (!selectedVenueData) {
      throw new Error('Please select a location.');
    }
    const selectedPitchData = resolvePitchForVenue(selectedVenueData, formData, pitchOptions);
    return { selectedVenueData, selectedPitchData };
  }
  const fallbackName =
    formData.customVenueName?.trim() ||
    formData.trainingLocation?.trim() ||
    academyName ||
    'Training';
  return {
    selectedVenueData: { id: null, name: fallbackName, pitches: [] },
    selectedPitchData: {
      slug: 'training',
      name: formData.customPitchName?.trim() || fallbackName,
    },
  };
}

/** @deprecated Use getPitchOptionsForCameraSetup with a replay venue row. */
export const getBFAPitchesFromMapping = (sport = 'Football') =>
  getPitchOptionsForCameraSetup({ source: 'replay', name: 'Beirut Football Academy', pitches: [] }, sport);


export const generateMatchKey = () => Math.random().toString(36).substring(2, 8).toUpperCase();

export const sendNotificationEmail = async (email, name, type = 'gameCreated') => {
  if (!email) return;

  try {
    const { error } = await supabase.functions.invoke('notify-stats', {
      body: { email, name, type },
    });

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error sending notification:', error);
  }
};

// Helper function to send notifications to all admins
export const notifyAdmins = async (gameDetails) => {
  try {
    // Get all admin users
    const { data: adminUsers, error: adminError } = await supabase
      .from('Users')
      .select('name, email')
      .not('admin_Pass', 'is', null)
      .not('admin_Pass', 'eq', '')
      .gt('admin_Pass', '');

    if (adminError) throw adminError;

    console.log(`Found ${adminUsers?.length || 0} admins to notify about new game`);

    // Send notifications to all admins using the admin template
    for (const admin of adminUsers || []) {
      if (!admin.email) continue;
      await notifyStats({
        email: admin.email,
        name: admin.name || 'Admin',
        type: 'gameCreatedAdmin',
        gameDetails,
      });
    }
  } catch (error) {
    console.error("Error notifying admins:", error);
    // Don't throw to avoid breaking the game creation flow
  }
};

export const parseDateInput = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

/** True when the game start (local date + time) is before now. */
export const isGameStartInPast = (gameDateStr, timeStr, now = new Date()) => {
  const gameDay = parseDateInput(gameDateStr);
  if (!gameDay) return false;
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (gameDay < todayStart) return true;
  if (gameDay > todayStart) return false;
  if (!timeStr || timeStr.length < 4) return false;
  const [hh, mm] = timeStr.substring(0, 5).split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(hh)) return false;
  const gameStart = new Date(
    gameDay.getFullYear(),
    gameDay.getMonth(),
    gameDay.getDate(),
    hh || 0,
    Number.isNaN(mm) ? 0 : mm,
    0,
    0,
  );
  return gameStart < now;
};

// Parse date string as local date (no timezone conversion)
export const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  // Handle both date strings and Date objects
  if (dateString instanceof Date) {
    return new Date(dateString.getFullYear(), dateString.getMonth(), dateString.getDate());
  }
  // Parse YYYY-MM-DD format as local date
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

// Format date to YYYY-MM-DD string (local date, no timezone conversion)
export const formatLocalDateString = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : parseLocalDate(date);
  if (!d || isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDateInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Snap HH:mm (or HH:mm:ss) to nearest 30-minute slot */
export const snapTimeToHalfHour = (timeValue) => {
  if (!timeValue) return '';
  const parts = String(timeValue).trim().split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? 0);
  if (Number.isNaN(h) || Number.isNaN(m)) return parts[0]?.length >= 5 ? String(timeValue).substring(0, 5) : '';
  const total = h * 60 + m;
  const rounded = Math.round(total / 30) * 30;
  const rh = Math.floor(rounded / 60) % 24;
  const rm = rounded % 60;
  return `${String(rh).padStart(2, '0')}:${String(rm).padStart(2, '0')}`;
};

/** Round a Date (or now) to nearest 30-minute slot as HH:mm */
export const roundTimeToHalfHour = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date();
  const totalMins = d.getHours() * 60 + d.getMinutes();
  const rounded = Math.round(totalMins / 30) * 30;
  const h = Math.floor(rounded / 60) % 24;
  const m = rounded % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** 12-hour label for HH:mm (e.g. "2:30 PM") */
export const formatTime12h = (timeValue) => {
  if (!timeValue) return '—';
  const parts = String(timeValue).split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return timeValue;
  const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${dh}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

/** Half-hour slots between startHour and endHour (inclusive), 30-minute steps */
export const buildHalfHourTimeOptions = (startHour = 6, endHour = 23) => {
  const options = [];
  for (let h = startHour; h <= endHour; h += 1) {
    for (let m = 0; m < 60; m += 30) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      options.push({ value, label: formatTime12h(value) });
    }
  }
  return options;
};

export const buildRecurringDates = (startDateStr, repeatWeeksValue, repeatEndDateStr) => {
  if (!startDateStr) return [];

  const repeatWeeks = Number(repeatWeeksValue) || 0;
  const startDate = parseDateInput(startDateStr);
  if (!startDate) return [];

  if (repeatWeeks <= 0) {
    return [formatDateInput(startDate)];
  }

  const endDate = parseDateInput(repeatEndDateStr);
  if (!endDate) {
    return [formatDateInput(startDate)];
  }

  if (endDate < startDate) {
    throw new Error('Repeat end date must be on or after the start date.');
  }

  const occurrences = [];
  const intervalDays = repeatWeeks * 7;
  let current = new Date(startDate);
  let safetyCounter = 0;
  while (current <= endDate) {
    occurrences.push(formatDateInput(current));
    current = new Date(current);
    current.setDate(current.getDate() + intervalDays);
    safetyCounter += 1;
    if (safetyCounter > 520) {
      throw new Error('Recurring schedule would create more than 520 matches. Please shorten the range.');
    }
  }

  return occurrences;
};
